import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BookOpen, Users, Bus, Palette, Trophy, TrendingUp, ArrowRight, MessageCircle, Sparkles } from "lucide-react";
import { IMAGES, SCHOOL } from "@/lib/site";
import ReviewsMarquee from "@/components/ReviewsMarquee";
import NoticesTeaser from "@/components/NoticesTeaser";
import IslamicValues from "@/components/IslamicValues";

const features = [
  { icon: BookOpen, title: "Quality Education", desc: "Comprehensive State Board curriculum with modern teaching methods and digital classrooms." },
  { icon: Users, title: "Expert Faculty", desc: "Experienced and dedicated teachers who mentor every child personally." },
  { icon: Bus, title: "Transportation", desc: "Safe and reliable school bus service covering all major Chikkamagaluru routes." },
  { icon: Palette, title: "Creative Arts", desc: "Fostering creativity through arts, crafts, music, dance & cultural programs." },
  { icon: Trophy, title: "Sports Excellence", desc: "District-level sports facilities and a championship-winning athletic culture." },
  { icon: TrendingUp, title: "Proven Results", desc: "Consistent 100% pass rate with students excelling in board exams year after year." },
];

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08 } }),
};

export default function HomePage() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative min-h-[92vh] flex items-center overflow-hidden"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${IMAGES.hero})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-royal/95 via-royal/85 to-royal/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-royal-dark/90 via-transparent to-transparent" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-24 w-full">
          <motion.div
            initial="hidden" animate="show" variants={fadeUp}
            className="max-w-4xl"
          >
            <div className="inline-flex items-center gap-2 border border-gold/60 bg-white/10 backdrop-blur-sm px-5 py-2 rounded-full text-gold font-semibold tracking-[0.25em] text-xs mb-8">
              <Sparkles size={14} /> ADMISSIONS OPEN
            </div>

            <h1 className="font-serif-display text-white text-5xl md:text-7xl lg:text-8xl font-bold leading-[1.05] tracking-tight">
              Welcome to <br/>
              <span className="text-gold">Royal English</span><br/>
              <span className="font-medium">High School</span>
            </h1>

            <p className="mt-8 text-lg md:text-xl text-gray-100/90 max-w-2xl leading-relaxed">
              Empowering young minds in Chikkamagaluru with quality education, values and skills for a brighter future. Where excellence meets tradition since 1999.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link to="/admission" data-testid="hero-apply-btn"
                className="bg-gold hover:bg-white text-royal px-8 py-4 rounded-full text-base font-bold inline-flex items-center justify-center gap-2 shadow-2xl transition-all hover:scale-[1.02]">
                Apply for Admission <ArrowRight size={18} />
              </Link>
              <Link to="/about" data-testid="hero-discover-btn"
                className="border border-white/60 bg-white/10 backdrop-blur-md text-white px-8 py-4 rounded-full text-base font-bold hover:bg-white hover:text-royal transition-all inline-flex items-center justify-center gap-2">
                Discover More
              </Link>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16 max-w-3xl">
              {[
                { v: "25+", l: "Years of Excellence" },
                { v: "1000+", l: "Happy Students" },
                { v: "50+", l: "Expert Teachers" },
                { v: "100%", l: "Pass Rate" },
              ].map((s, i) => (
                <motion.div
                  key={s.l}
                  custom={i} initial="hidden" animate="show" variants={fadeUp}
                  className="text-center md:text-left"
                  data-testid={`hero-stat-${i}`}
                >
                  <h3 className="font-serif-display text-4xl md:text-5xl text-gold font-bold">{s.v}</h3>
                  <p className="text-white/80 text-sm mt-1">{s.l}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/70 text-xs tracking-[0.3em] flex flex-col items-center gap-2">
          <span>SCROLL</span>
          <div className="w-px h-10 bg-gradient-to-b from-gold to-transparent" />
        </div>
      </section>

      {/* ABOUT TEASER */}
      <section className="py-24 bg-white px-6" id="about-teaser">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-16 items-center">
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="relative">
            <img src={IMAGES.building} alt="Royal English School" className="rounded-3xl shadow-2xl w-full h-[520px] object-cover" />
            <div className="absolute -bottom-6 -right-6 md:right-8 bg-gold text-royal-dark px-8 py-6 rounded-2xl shadow-2xl">
              <h3 className="font-serif-display text-4xl font-bold">25+</h3>
              <p className="text-sm font-semibold">Years of Excellence</p>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <span className="inline-block bg-royal/5 text-royal px-5 py-1.5 rounded-full text-xs font-bold tracking-[0.25em]">ABOUT US</span>
            <h2 className="font-serif-display text-4xl md:text-5xl font-bold mt-6 leading-tight text-royal">
              Nurturing Future Leaders <span className="text-gold">Since 1999</span>
            </h2>
            <div className="gold-divider" />
            <p className="mt-6 text-lg text-gray-600 leading-relaxed">
              Royal English High School, run by the Royal Education Society (R.), was established with the vision of providing world-class education that combines academic excellence with character development for the children of Chikkamagaluru.
            </p>
            <div className="grid md:grid-cols-2 gap-5 mt-8">
              <div className="border-l-4 border-royal bg-[#f7f7f7] p-5 rounded-r-2xl">
                <h3 className="font-serif-display text-xl font-bold">Our Vision</h3>
                <p className="mt-2 text-gray-600 text-sm">To nurture responsible global citizens who contribute positively to society.</p>
              </div>
              <div className="border-l-4 border-gold bg-[#f7f7f7] p-5 rounded-r-2xl">
                <h3 className="font-serif-display text-xl font-bold">Our Mission</h3>
                <p className="mt-2 text-gray-600 text-sm">Deliver excellence through innovative teaching and holistic development.</p>
              </div>
            </div>
            <Link to="/about" data-testid="home-about-cta"
              className="mt-8 inline-flex items-center gap-2 bg-royal hover:bg-royal-dark text-white px-7 py-3.5 rounded-full font-bold shadow-xl transition-all">
              Learn More <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-24 bg-[#f5f5f5] px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block bg-gold/15 text-gold-dark px-5 py-1.5 rounded-full text-xs font-bold tracking-[0.25em]">WHY CHOOSE US</span>
            <h2 className="font-serif-display text-4xl md:text-5xl font-bold mt-6 text-royal">What Makes Us Special</h2>
            <div className="w-16 h-1 bg-gold mx-auto mt-4 rounded-full" />
            <p className="mt-6 text-lg text-gray-600">We provide a nurturing environment where every child can discover their potential and excel.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-7">
            {features.map((f, i) => (
              <motion.div
                key={f.title}
                custom={i} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }} variants={fadeUp}
                className="bg-white rounded-2xl p-8 hover-lift border border-gray-100"
                data-testid={`feature-${i}`}
              >
                <div className="w-14 h-14 rounded-2xl bg-royal/5 flex items-center justify-center text-royal mb-6">
                  <f.icon size={26} />
                </div>
                <h3 className="font-serif-display text-2xl font-bold text-royal-dark mb-2">{f.title}</h3>
                <p className="text-gray-600 leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ISLAMIC VALUES */}
      <IslamicValues />

      {/* ACADEMICS STRIP */}
      <section className="py-24 bg-royal text-white px-6">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-14 items-center">
          <div>
            <span className="inline-block bg-white/10 text-gold px-5 py-1.5 rounded-full text-xs font-bold tracking-[0.25em]">ACADEMICS</span>
            <h2 className="font-serif-display text-4xl md:text-5xl font-bold mt-6 leading-tight">
              Comprehensive <span className="text-gold">Curriculum</span>
            </h2>
            <div className="gold-divider" />
            <p className="mt-6 text-lg text-gray-200 leading-relaxed">
              Our curriculum is designed to provide a well-rounded education that prepares students for future challenges.
            </p>
            <ul className="mt-8 space-y-3">
              {[
                "State Board Curriculum (1st to 10th Standard)",
                "English Medium Instruction",
                "Computer Education & Digital Literacy",
                "Science & Mathematics Labs",
                "Language Programs (Kannada, Hindi, English)",
                "Value-based Education",
              ].map((c, i) => (
                <li key={i} className="flex gap-3 items-start">
                  <span className="w-2 h-2 rounded-full bg-gold mt-2.5 shrink-0" />
                  <span className="text-base">{c}</span>
                </li>
              ))}
            </ul>
            <Link to="/academics" data-testid="home-academics-cta"
              className="mt-10 inline-flex items-center gap-2 bg-gold text-royal-dark hover:bg-white px-7 py-3.5 rounded-full font-bold transition-all">
              Explore Academics <ArrowRight size={16} />
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-5">
            <img src={IMAGES.graduation} alt="Graduation" className="rounded-2xl h-[300px] object-cover w-full shadow-2xl" />
            <img src={IMAGES.sports} alt="Sports Day" className="rounded-2xl h-[300px] object-cover w-full mt-10 shadow-2xl" />
            <img src={IMAGES.group} alt="Students Group" className="rounded-2xl h-[300px] object-cover w-full -mt-6 col-span-2 shadow-2xl" />
          </div>
        </div>
      </section>

      {/* NOTICES TEASER */}
      <NoticesTeaser />

      {/* REVIEWS MARQUEE */}
      <ReviewsMarquee />

      {/* CTA */}
      <section className="py-24 px-6 bg-[#f5f5f5]">
        <div className="max-w-6xl mx-auto bg-royal-dark text-white rounded-3xl p-12 md:p-16 relative overflow-hidden">
          <div className="absolute -right-20 -top-20 w-80 h-80 rounded-full bg-gold/20 blur-3xl" />
          <div className="relative grid md:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="font-serif-display text-4xl md:text-5xl font-bold leading-tight">
                Ready to give your child <span className="text-gold">the best?</span>
              </h2>
              <p className="mt-5 text-gray-200 text-lg">Admissions are now open. Visit our campus or get in touch with us today.</p>
            </div>
            <div className="flex flex-col sm:flex-row md:flex-col gap-4 md:items-end">
              <Link to="/admission" data-testid="cta-apply-btn"
                className="bg-gold text-royal-dark px-8 py-4 rounded-full font-bold hover:bg-white transition-all text-center inline-flex items-center justify-center gap-2">
                Apply Now <ArrowRight size={16} />
              </Link>
              <a href={`https://wa.me/${SCHOOL.whatsapp}`} target="_blank" rel="noreferrer" data-testid="cta-whatsapp-btn"
                className="border border-white/40 px-8 py-4 rounded-full font-bold hover:bg-white/10 transition-all text-center inline-flex items-center justify-center gap-2">
                <MessageCircle size={18} /> WhatsApp Us
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
